/**
 * Single source of truth for cart + checkout order totals (Alberta: GST 5%).
 * Line subtotal is sum(price × qty); promo is applied before tax; delivery and tips are in the GST base.
 */

/** Federal GST rate for Alberta retail (no provincial component in this model). */
export const GST_RATE = 0.05;

/** Display label for the tax line in order summaries (keep in sync with account order history). */
export const ORDER_TAX_LABEL = "Taxes (GST 5%)";

export type OrderTotalsInput = {
  /** Sum of line items before promo (from cart `total`). */
  lineSubtotal: number;
  promoDiscount: number;
  deliveryFee: number;
  tipAmount: number;
};

export type OrderTotalsResult = OrderTotalsInput & {
  /** Amount GST applies to (never negative). */
  taxableAmount: number;
  tax: number;
  finalTotal: number;
};

/**
 * `preTaxTotal` = subtotal − promo + delivery + tip.
 * GST is 5% of max(0, preTaxTotal) so tax is never negative when the basket is invalid.
 * `finalTotal` = preTaxTotal + tax.
 */
export function computeOrderTotals(input: OrderTotalsInput): OrderTotalsResult {
  const lineSubtotal = Number(input.lineSubtotal) || 0;
  const promoDiscount = Math.max(0, Number(input.promoDiscount) || 0);
  const deliveryFee = Math.max(0, Number(input.deliveryFee) || 0);
  const tipAmount = Math.max(0, Number(input.tipAmount) || 0);

  const preTaxTotal = lineSubtotal - promoDiscount + deliveryFee + tipAmount;
  const taxableAmount = Math.max(0, preTaxTotal);
  const tax = taxableAmount * GST_RATE;
  const finalTotal = preTaxTotal + tax;

  return {
    lineSubtotal,
    promoDiscount,
    deliveryFee,
    tipAmount,
    taxableAmount,
    tax,
    finalTotal,
  };
}
